<template>
  <div class="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
    <!-- Hero：avatar + 徽章 + 名稱 + 成員數（精簡；save/preview/help 由外層 PageHeader actions slot 注入） -->
    <div class="px-4 py-3 flex items-center gap-3 border-b border-slate-200">
      <div
        class="w-9 h-9 rounded-lg flex items-center justify-center text-white text-sm font-bold shrink-0"
        :class="tab === 'department' ? 'bg-teal-500' : 'bg-indigo-500'"
      >
        {{ selected.name.slice(0, 1) }}
      </div>
      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-1.5 flex-wrap">
          <span
            class="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold"
            :class="tab === 'department' ? 'bg-teal-100 text-teal-700' : 'bg-indigo-100 text-indigo-700'"
          >
            {{ roleLabel }}
          </span>
          <h2 class="text-sm font-semibold text-slate-800 truncate">{{ selected.name }}</h2>
          <span class="text-[11px] text-slate-500 shrink-0">
            · {{ memberCount }} {{ $t('permission_member_count_suffix') }}
          </span>
        </div>
      </div>
    </div>

    <!-- Matrix table -->
    <div v-if="!loading && visibleNodes.length" class="overflow-auto max-h-[calc(100vh-14rem)]">
      <table class="min-w-full border-collapse">
        <thead class="sticky top-0 z-10 bg-slate-50">
          <tr class="border-b border-slate-200">
            <th class="sticky left-0 z-20 bg-slate-50 text-left px-4 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-64">
              {{ $t('permission_col_resource') }}
            </th>
            <th
              v-for="a in ACTIONS"
              :key="a"
              class="text-center px-2 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider min-w-[150px]"
            >
              {{ $t('permission_action_' + a) }}
            </th>
            <th class="text-center px-2 py-2.5 text-[11px] font-semibold text-slate-500 uppercase tracking-wider w-24">
              {{ $t('permission_col_shortcut') }}
            </th>
          </tr>
        </thead>
        <tbody class="divide-y divide-slate-100/60">
          <tr
            v-for="node in visibleNodes"
            :key="node.resource.key"
            class="hover:bg-indigo-50/60 transition-colors duration-200 group"
          >
            <!-- Resource cell with indent + expander + kind badge + description -->
            <td class="sticky left-0 z-10 bg-white group-hover:bg-indigo-50/60 px-4 py-3 align-top transition-colors duration-200">
              <div class="flex items-start gap-1.5" :style="{ paddingLeft: (node.depth * 16) + 'px' }">
                <button
                  v-if="!node.isLeaf"
                  type="button"
                  class="flex items-center justify-center w-4 h-4 text-slate-400 hover:text-slate-700 cursor-pointer shrink-0 mt-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 rounded"
                  :aria-label="isCollapsed(node.resource.key) ? $t('permission_expand_aria') : $t('permission_collapse_aria')"
                  :aria-expanded="!isCollapsed(node.resource.key)"
                  @click="toggleCollapse(node.resource.key)"
                >
                  <v-icon size="14" color="currentColor">
                    {{ isCollapsed(node.resource.key) ? 'mdi-chevron-right' : 'mdi-chevron-down' }}
                  </v-icon>
                </button>
                <span v-else class="w-4 shrink-0" aria-hidden="true"></span>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-1.5 flex-wrap">
                    <span class="text-sm font-medium text-slate-800 truncate">{{ node.resource.displayName }}</span>
                    <span
                      class="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[10px] font-semibold border shrink-0"
                      :class="kindMeta(node.resource).chipClass"
                    >
                      <v-icon size="10" :color="kindIconColor(kindOf(node.resource))">
                        {{ kindMeta(node.resource).icon }}
                      </v-icon>
                      {{ $t(kindMeta(node.resource).labelKey) }}
                    </span>
                    <span
                      v-if="node.resource.isPreview"
                      class="inline-flex items-center px-1.5 py-0 rounded text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200"
                    >
                      {{ $t('permission_preview_badge') }}
                    </span>
                    <v-icon
                      v-if="node.resource.description"
                      size="12"
                      color="#94a3b8"
                      class="cursor-help shrink-0"
                      :title="node.resource.description"
                    >
                      mdi-information-outline
                    </v-icon>
                  </div>
                  <div class="text-[11px] font-mono text-slate-400 mt-0.5 truncate">{{ node.resource.key }}</div>
                </div>
              </div>
            </td>

            <!-- Action cells — card-style toggle，視覺對齊 UsersView 的 PermissionCellToggle -->
            <td
              v-for="a in ACTIONS"
              :key="a"
              class="px-1.5 py-2 text-center align-middle"
            >
              <template v-if="supportsAction(node.resource, a)">
                <div
                  v-if="isLegacyResource(node)"
                  class="rounded-lg bg-slate-50 border border-slate-200 text-[11px] text-slate-400 py-3 px-2 text-center leading-tight"
                  :title="$t('permission_cell_inherited_legacy')"
                >
                  {{ $t('permission_cell_inherited_legacy') }}
                </div>
                <div v-else class="inline-flex flex-col items-stretch gap-1 w-full">
                  <button
                    type="button"
                    role="switch"
                    :aria-checked="cellState(node, a) === 'full'"
                    :aria-label="cellAriaLabel(node, a)"
                    :disabled="node.resource.isPreview || node.resource.isInCatalog === false"
                    class="rounded-lg p-2 text-left w-full max-w-[198px] mx-auto transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300"
                    :class="[cellWrapClass(node, a), (node.resource.isPreview || node.resource.isInCatalog === false) ? 'cursor-not-allowed opacity-60' : 'cursor-pointer']"
                    @click="onToggleCell(node, a)"
                  >
                    <div class="flex items-center gap-1.5">
                      <span
                        class="inline-block w-2 h-2 rounded-full shrink-0"
                        :class="cellDotClass(node, a)"
                        aria-hidden="true"
                      />
                      <span class="text-[12px] font-semibold" :class="cellLabelClass(node, a)">
                        {{ $t(cellLabelKey(node, a)) }}
                      </span>
                    </div>
                  </button>
                  <!-- Scope chip — 僅當 resource 支援 scope 且 cell=full 時顯示 -->
                  <permission-scope-chip
                    v-if="node.resource.supportsScope && (node.resource.isPreview || cellState(node, a) === 'full')"
                    :scope="cellScope(node, a)"
                    :disabled="node.resource.isPreview || node.resource.isInCatalog === false"
                    @change="s => onChangeScope(node, a, s)"
                  />
                </div>
              </template>
              <!-- 不支援此動作：輕量佔位，避免 row 視覺破格 -->
              <div v-else class="rounded-lg bg-slate-50/60 border border-dashed border-slate-200 text-[10px] text-slate-300 py-3 px-2" :title="$t('permission_cell_na')">
                {{ $t('permission_cell_na') }}
              </div>
            </td>

            <!-- Shortcut -->
            <td class="px-2 py-2 text-center">
              <div v-if="!node.resource.isPreview && !isLegacyResource(node) && node.resource.isInCatalog !== false" class="inline-flex items-center gap-1">
                <button
                  type="button"
                  class="px-1.5 py-0.5 text-[11px] rounded-md bg-indigo-50 text-indigo-600 hover:bg-indigo-100 transition-colors duration-200 cursor-pointer font-medium"
                  @click="onSetRowAll(node, true)"
                >
                  {{ $t('permission_select_all_short') }}
                </button>
                <button
                  type="button"
                  class="px-1.5 py-0.5 text-[11px] rounded-md bg-slate-100 text-slate-500 hover:bg-slate-200 transition-colors duration-200 cursor-pointer font-medium"
                  @click="onSetRowAll(node, false)"
                >
                  {{ $t('permission_clear_short') }}
                </button>
              </div>
              <span v-else-if="node.resource.isPreview" class="text-[10px] text-amber-700 font-medium">{{ $t('permission_preview_readonly') }}</span>
              <span v-else class="text-[10px] text-slate-300" aria-hidden="true">—</span>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- hideLegacy 把資源全篩掉時的 empty state，避免誤以為資源不見 -->
    <div v-else-if="!loading" class="px-4 py-10 text-center text-sm text-slate-400">
      {{ $t('permission_legacy_all_hidden') }}
    </div>

    <!-- Loading skeleton — 對應 1 寬欄 + 6 窄欄 + shortcut，減少 CLS -->
    <div v-else class="p-4 space-y-3">
      <div v-for="i in 4" :key="i" class="animate-pulse flex gap-2">
        <div class="h-16 bg-slate-100 rounded-lg w-64 shrink-0"></div>
        <div v-for="j in 6" :key="j" class="h-16 bg-slate-50 rounded-lg flex-1 min-w-0"></div>
        <div class="h-16 bg-slate-50 rounded-lg w-24 shrink-0"></div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ACTIONS,
  SCOPE,
  supportsAction,
  kindOf,
  kindMeta,
  kindIconColor
} from '@/js/permission-helpers'
import permissionTreeMixin from '@/js/permission-tree-mixin'
import PermissionScopeChip from './PermissionScopeChip.vue'

// cellState tone — 與 UsersView PermissionCellToggle 語義對齊（同用 dot + label + tone wrap）：
// - full：完全允許 → indigo 強調（跟 override-grant 一致）
// - partial：部分允許 → indigo 弱化（讓使用者一眼看出「還沒全開」）
// - none：未授權 → 中性白底灰字
const CELL_TONE = {
  full: {
    wrap: 'bg-indigo-50 ring-1 ring-indigo-200',
    dot: 'bg-indigo-500',
    label: 'text-indigo-700',
    labelKey: 'permission_role_cell_full'
  },
  partial: {
    wrap: 'bg-indigo-50/60 ring-1 ring-indigo-100',
    dot: 'bg-indigo-300',
    label: 'text-indigo-700',
    labelKey: 'permission_role_cell_partial'
  },
  none: {
    wrap: 'bg-white ring-1 ring-slate-200',
    dot: 'bg-slate-300',
    label: 'text-slate-400',
    labelKey: 'permission_role_cell_none'
  }
}

export default {
  name: 'RoleMatrix',
  components: { PermissionScopeChip },
  mixins: [permissionTreeMixin],
  props: {
    tab: { type: String, default: 'department' },
    selected: { type: Object, required: true },
    matrix: { type: Object, default: () => ({}) },
    loading: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    dirty: { type: Boolean, default: false },
    memberCount: { type: [String, Number], default: '—' }
  },
  data () {
    return { ACTIONS }
  },
  computed: {
    roleLabel () {
      return this.tab === 'department'
        ? this.$t('permission_dept_short')
        : this.$t('permission_group_short')
    }
  },
  methods: {
    supportsAction,
    kindOf,
    kindMeta,
    kindIconColor,
    isGranted (resourceKey, action) {
      const m = this.matrix[resourceKey] && this.matrix[resourceKey][action]
      return !!(m && m.isGranted)
    },
    // Legacy 軌 resource 走舊版 Role 檢查、不受群組/部門矩陣控制
    isLegacyResource (node) {
      return !!(node.resource && node.resource.useLegacyPermission)
    },
    cellScope (node, action) {
      const m = this.matrix[node.resource.key] && this.matrix[node.resource.key][action]
      if (m && m.scope !== null && m.scope !== undefined) return Number(m.scope)
      return SCOPE.ALL
    },
    onChangeScope (node, action, scope) {
      if (node.resource.isPreview || node.resource.isInCatalog === false) return
      this.$emit('set-scope', { res: node.resource, action, scope })
    },
    // cellState: 'full' | 'partial' | 'none'
    // - leaf：granted → full；else none
    // - parent：self + descendants 全勾 full；部分 partial；全沒勾 none
    cellState (node, action) {
      if (node.isLeaf) {
        return this.isGranted(node.resource.key, action) ? 'full' : 'none'
      }
      const keys = [node.resource.key, ...this.descendantKeysCached(node.resource.key)]
      let grantedCount = 0
      let supportCount = 0
      for (const k of keys) {
        const r = this.resourceByKey.get(k)
        // legacy resource 不存矩陣紀錄，排除以免 parent 永遠卡 partial
        if (!r || r.useLegacyPermission || !supportsAction(r, action)) continue
        supportCount++
        if (this.isGranted(k, action)) grantedCount++
      }
      if (supportCount === 0) return 'none'
      if (grantedCount === 0) return 'none'
      if (grantedCount === supportCount) return 'full'
      return 'partial'
    },
    cellWrapClass (node, a) { return CELL_TONE[this.cellState(node, a)].wrap },
    cellDotClass (node, a) { return CELL_TONE[this.cellState(node, a)].dot },
    cellLabelClass (node, a) { return CELL_TONE[this.cellState(node, a)].label },
    cellLabelKey (node, a) { return CELL_TONE[this.cellState(node, a)].labelKey },
    cellAriaLabel (node, a) {
      const stateLabel = this.$t(CELL_TONE[this.cellState(node, a)].labelKey)
      const actionLabel = this.$t('permission_action_' + a)
      return `${node.resource.displayName} ${actionLabel}: ${stateLabel}`
    },
    onToggleCell (node, action) {
      // true-orphan（DB 殘留、catalog 外）反灰全鎖：前端擋 draft，後端 catalog gate 為後盾
      if (node.resource.isPreview || node.resource.useLegacyPermission || node.resource.isInCatalog === false) return
      const s = this.cellState(node, action)
      const nextValue = s !== 'full'
      this.broadcastCell(node, action, nextValue)
    },
    broadcastCell (node, action, value) {
      if (node.resource.isPreview || node.resource.useLegacyPermission || node.resource.isInCatalog === false) return
      if (supportsAction(node.resource, action)) {
        this.$emit('toggle-cell', { res: node.resource, action, value })
      }
      for (const k of this.descendantKeysCached(node.resource.key)) {
        const r = this.resourceByKey.get(k)
        if (r && !r.isPreview && !r.useLegacyPermission && r.isInCatalog !== false && supportsAction(r, action)) {
          this.$emit('toggle-cell', { res: r, action, value })
        }
      }
    },
    onSetRowAll (node, enabled) {
      if (node.resource.isPreview || node.resource.useLegacyPermission || node.resource.isInCatalog === false) return
      this.$emit('set-row-all', { res: node.resource, enabled })
      for (const k of this.descendantKeysCached(node.resource.key)) {
        const r = this.resourceByKey.get(k)
        if (r && !r.isPreview && !r.useLegacyPermission && r.isInCatalog !== false) this.$emit('set-row-all', { res: r, enabled })
      }
    }
  }
}
</script>
